import { ChangeEventHandler, FC, useCallback, useState } from 'react';
import {
  CarAllocation,
  CarAllocationGroupBy,
  GetCarAllocationCondition,
} from '../../../../types/carAllocation';
import styles from './styles.module.css';
import Conditions from './Conditions';
import classNames from 'classnames';
import Select from '../../../../components/common/Select';
import DriversTable from './DriverTable';
import CarsTable from './CarsTable';
import EditAllocationModal from './EditAllocationModal';
import Popup from '../../../../components/common/Popup';
import AllocationDetails from './AllocationDetails';
import TimelineHead from './TimelineHead';
import NoResultFound from '../../../../components/common/NoResultFound';
import {
  useGetAllocationsInCarOrderQuery,
  useGetAllocationsInDriverOrderQuery,
} from '../../../../api/carAllocationApi';
import PolicyRequired from '../../../../components/common/PolicyRequired';
import { FooterLayoutCenterize } from '../../../../components/FooterLayout';

type Props = {
  initConditions: GetCarAllocationCondition;
  conditions: GetCarAllocationCondition;
  onConditionChange: (conditions: GetCarAllocationCondition) => void;
  setLastCalculatedAt: (lastCalculatedAt: string) => void;
  isToastPopUp: boolean;
};
const DayAllocations: FC<Props> = ({
  conditions,
  onConditionChange,
  initConditions,
  setLastCalculatedAt,
  isToastPopUp,
}) => {
  const [groupBy, setGroupBy] = useState<CarAllocationGroupBy>('driver');
  const groupByOptions = [
    { label: 'ドライバー', value: 'driver' },
    { label: '車両', value: 'car' },
  ];
  const groupByChangeHandler: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newGroupBy = e.target.value;
    if (newGroupBy !== 'driver' && newGroupBy !== 'car') {
      return;
    }
    setGroupBy(newGroupBy);
  };

  const [popupState, setPopupState] = useState<PopupState>();
  const closePopup = useCallback(() => setPopupState(undefined), []);

  const { data: allocationsDriver } = useGetAllocationsInDriverOrderQuery(
    conditions,
    {
      skip: groupBy !== 'driver',
    }
  );
  const { data: allocationsCar } = useGetAllocationsInCarOrderQuery(
    conditions,
    {
      skip: groupBy !== 'car',
    }
  );

  const noResult =
    (groupBy === 'driver' ? allocationsDriver?.drivers : allocationsCar?.cars)
      ?.length === 0;

  return (
    <>
      <PolicyRequired policy={34}>
        <Conditions
          conditions={conditions}
          onChange={onConditionChange}
          initConditions={initConditions}
        />
      </PolicyRequired>
      {!noResult ? (
        <div
          className={classNames(
            styles.timelines,
            isToastPopUp && styles.isToastPopUp
          )}
        >
          <div className={styles.timelineHeadRow}>
            <div
              className={classNames(
                styles.timelineHead,
                styles.timelineGroupKeyCell,
                styles.groupByCell
              )}
            >
              <div className={styles.groupBy}>
                表示順
                <div className={styles.groupBySelect}>
                  <Select
                    options={groupByOptions}
                    value={groupBy}
                    onChange={groupByChangeHandler}
                    variants={['fit', 'mini']}
                  />
                </div>
              </div>
            </div>
            <TimelineHead />
          </div>
          {groupBy === 'driver' ? (
            <DriversTable
              allocations={allocationsDriver}
              originDate={new Date(conditions.startDate)}
              setLastCalculatedAt={setLastCalculatedAt}
              onClickDriverTime={(elem, allocation, userId, carId) =>
                setPopupState({ reference: elem, allocation, userId, carId })
              }
            />
          ) : (
            <CarsTable
              allocations={allocationsCar}
              originDate={new Date(conditions.startDate)}
              setLastCalculatedAt={setLastCalculatedAt}
              onClickDriverTime={(elem, allocation, userId, carId) =>
                setPopupState({ reference: elem, allocation, userId, carId })
              }
            />
          )}
          <EditAllocationModal></EditAllocationModal>
        </div>
      ) : (
        <FooterLayoutCenterize>
          <div className={styles.noResultFound}>
            <NoResultFound />
          </div>
        </FooterLayoutCenterize>
      )}
      <PolicyRequired policy={30}>
        {popupState && (
          <Popup
            reference={popupState.reference}
            float
            onRequestClose={closePopup}
          >
            <AllocationDetails
              allocations={[popupState.allocation]}
              onRequestClose={closePopup}
              userId={popupState.userId}
              carId={popupState.carId}
            />
          </Popup>
        )}
      </PolicyRequired>
    </>
  );
};
export default DayAllocations;

type PopupState =
  | {
      reference: HTMLElement;
      allocation: CarAllocation;
      userId?: number | null;
      carId?: number | null;
    }
  | undefined;
